import { Stack, Typography } from "@mui/material";
import { CrudLink, CustomCellDate } from "@/shared/components";
import type { CrudTotalSchemaItem } from "@/shared/components/CRUD/CrudTotal";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import type { InternalExpensesListRow } from "@/admin/entities/internalExpenses";
import type { InternalExpensesListQueryType } from "@/types/graphql/admin.generated";
import { formatMoney } from "../domains";
import { INTERNAL_EXPENSE_CATEGORY_LABEL } from "./expensesSchemas";

export function getTableData(data: unknown) {
  const result = (data as InternalExpensesListQueryType)?.internalExpenses;
  return result?.nodes ? result : { nodes: [], count: 0 };
}

export function mapColumns(expense: InternalExpensesListRow) {
  return [
    expense.id,
    expense,
    expense.category,
    expense.vendor,
    expense.amount,
    expense.spentAt,
    expense.createdAt,
  ];
}

export const getColumnsConfig =
  (): ColumnDefinition<InternalExpensesListRow>[] => [
    { name: "id", label: "ID", options: { display: false, filter: false } },
    {
      name: "title",
      label: "Расход",
      options: {
        customBodyRender: (data: unknown) => {
          const expense = data as InternalExpensesListRow;
          return (
            <CrudLink tab="main" id={expense.id}>
              {expense.title}
            </CrudLink>
          );
        },
      },
    },
    {
      name: "category",
      label: "Категория",
      options: {
        customBodyRender: (value: unknown) =>
          INTERNAL_EXPENSE_CATEGORY_LABEL[String(value)] ?? String(value),
      },
    },
    { name: "vendor", label: "Поставщик", options: {} },
    {
      name: "amount",
      label: "Сумма",
      options: {
        customBodyRender: (value: unknown) =>
          formatMoney(value as InternalExpensesListRow["amount"]),
      },
    },
    {
      name: "spentAt",
      label: "Дата расхода",
      options: {
        customBodyRender: (value: unknown) => (
          <CustomCellDate date={value as string} />
        ),
      },
    },
    {
      name: "createdAt",
      label: "Добавлен",
      options: {
        customBodyRender: (value: unknown) => (
          <CustomCellDate date={value as string} />
        ),
      },
    },
  ];

/** Итог по всей выборке с учётом фильтров, а не только по текущей странице. */
export const getTotalSchema = (): CrudTotalSchemaItem[] => [
  {
    xs: 12,
    md: 4,
    label: "Итого по фильтру",
    value: (data: unknown) => {
      const result = (data as InternalExpensesListQueryType)?.internalExpenses;
      return formatMoney(result?.totalAmount);
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Наши траты",
  itemCreated: "Расход добавлен",
  itemUpdated: "Расход обновлён",
  createItem: "Добавить расход",
  refetchList: "Обновить список",
  deleteItems: "Удалить",
  itemsDeleted: "Расходы удалены",
  restoreItems: "Восстановить",
  itemsRestored: "Расходы восстановлены",
  cancel: "Отмена",
  editItem: "Расход",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<InternalExpensesListRow> => ({
    main: {
      title: "Расход",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography noWrap>{row.title}</Typography>
          <Typography variant="body2" color="textSecondary" noWrap>
            {INTERNAL_EXPENSE_CATEGORY_LABEL[row.category] ?? row.category}
          </Typography>
        </Stack>
      ),
    },
    amount: {
      title: "Сумма",
      renderCell: (row) => formatMoney(row.amount),
    },
  });
